import { canAccessResource } from '$lib/Auth';
import { COLLECTION_TYPES, getEntity } from '$lib/DB';

const localSeatAllocationCache = new Map();

function returnObject(isFull = false, isOverThreshold = false) {
    return {
        isFull,
        isOverThreshold,
    };
};

function coerceDate(value) {
    if (!value) {
        return null;
    }

    if (value instanceof Date && !isNaN(value)) {
        return value;
    }

    if (typeof value === 'object') {
        if (typeof value.toDate === 'function') {
            const date = value.toDate();
            return date instanceof Date && !isNaN(date) ? date : null;
        }

        if (typeof value.seconds === 'number') {
            const millis = (value.seconds * 1000) + (value.nanoseconds ? value.nanoseconds / 1e6 : 0);
            const date = new Date(millis);
            return !isNaN(date) ? date : null;
        }
    }

    const date = new Date(value);
    return !isNaN(date) ? date : null;
}

async function isOverSeatAllocation(subscriptionOrId, resourceId = null) {
    let subscription = subscriptionOrId;
    if (typeof subscriptionOrId === 'string') {
        subscription = (await getEntity({ id: subscriptionOrId }, COLLECTION_TYPES.SUBSCRIPTIONS))?.data();
    }

    if (!subscription) {
        console.error(`Subscription not found for resource: ${subscriptionOrId}`);
        return returnObject();
    }

    const { seats, seatHolders, students, courses, threshold } = subscription;

    const overThreshold = (seats, studentsNumber) => {
        if (!threshold) {
            return false;
        }

        if (Number(seats) === 0) {
            return false;
        }

        const over = studentsNumber - Number(seats);
        const overPercentage = (over / Number(seats)) * 100;

        return overPercentage > Number(threshold);
    };

    if (courses?.length) {
        // Check if any of the courses are objects
        const courseObjects = courses.filter(course => typeof course === 'object');
        if (courseObjects.length > 0) {

            if (resourceId) {
                const course = courseObjects.find(course => course.id === resourceId);
                if (course) {
                    const { students, seats } = course;
                    const studentsNumber = typeof students === 'object' ? students.length : students;
                    if (seats && studentsNumber) {
                        const isFull = ((studentsNumber || 0) >= Number(seats)) && (Number(seats) !== 0);
                        if (isFull) {
                            const isOverThreshold = overThreshold(seats, studentsNumber);
                            console.error(`Seat allocation is ${isOverThreshold ? 'over' : 'full'} for course: ${course.id}`);
                            return returnObject(isFull, isOverThreshold);
                        }
                    }

                    return returnObject();
                }
            }

            // Loop through the course objects and get the course limits and seats and students
            for (const course of courseObjects) {
                const { students, seats } = course;
                const studentsNumber = typeof students === 'object' ? students.length : students;
                if (seats && studentsNumber) {
                    const isFull = ((studentsNumber || 0) >= Number(seats)) && (Number(seats) !== 0);
                    if (isFull) {
                        const isOverThreshold = overThreshold(seats, studentsNumber);
                        console.error(`Seat allocation is ${isOverThreshold ? 'over' : 'full'} for course: ${course.id}`);
                        return returnObject(isFull, isOverThreshold);
                    }
                }
            }
        }
    }

    const studentsNumber = students !== undefined ? (students?.length || 0) : (seatHolders?.length || 0);
    const isFull = ((studentsNumber || 0) >= Number(seats)) && (Number(seats) !== 0);
    if (isFull) {
        const isOverThreshold = overThreshold(seats, studentsNumber);
        console.error(`Seat allocation is ${isOverThreshold ? 'over' : 'full'} for subscription: ${subscription.id}`);
        return returnObject(isFull, isOverThreshold);
    }

    return returnObject();
}

async function isResourceOverSeatAllocation(resourceId) {
    if (localSeatAllocationCache.has(resourceId)) {
        return localSeatAllocationCache.get(resourceId);
    }

    try {
        const subscriptionAndCourse = await canAccessResource(resourceId, true, true);

        const { subscription, courseId } = subscriptionAndCourse;

        const test = isOverSeatAllocation(subscription, courseId || resourceId || null);
        localSeatAllocationCache.set(resourceId, test);

        return test;
    } catch (error) {
        console.error(`Error checking seat allocation for resource: ${resourceId}`);

        return returnObject();
    }
}

function isSubscriptionExpired(subscription) {
    let expired = true;
    const status = subscription.active || null;
    const endDate = coerceDate(subscription.endDate);

    if (status === undefined || status === 'active' || status === 'checked' || status === 'true' || status === true) {
        if (!endDate || endDate > new Date()) {
            expired = false;
        }
    }

    return expired;
}

export { isOverSeatAllocation, isResourceOverSeatAllocation, isSubscriptionExpired };
